import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, useMsal, useAccount } from '@azure/msal-react';
import { apiService } from '../services/apiService';
import { partnerService } from '../services/partnerService';
import { PartnerProfile, PartnerAuthContext, Customer } from '../types/partner';

import { config } from '../config/environment';
import { msalInstance, currentAccount } from '../auth/msal';
import { 
  mockAccount, 
  mockPartnerProfile, 
  mockPartnerAuthContext, 
  mockAccessToken 
} from '../auth/mockAuth';
import { clearAitryxCoreTokenCache } from '../services/aitryxCoreTokenService';


// Scopes for API access
// Include the Core API scope at login time so the access token for the API
// is cached from the interactive flow. Without this, the very first API call
// has to go through a hidden iframe to AAD to acquire the API scope, which
// fails with `monitor_window_timeout` in browsers that partition third-party
// cookies on login.microsoftonline.com (Edge tracking prevention, Safari ITP,
// Chrome 3P-cookie phaseout).
const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'offline_access', config.azure.apiScope],
  prompt: 'select_account',
};

const apiScopes = {
  scopes: [config.azure.apiScope],
};

// msalInstance is imported from ../auth/msal

// Enhanced Auth context types - Updated for dual-role user model (Phase 3)
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  partner: PartnerProfile | null;
  partnerAuthContext: PartnerAuthContext | null;
  account: AccountInfo | null;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /** Leave the current Microsoft account and open the account picker. */
  signInWithAnotherAccount: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  clearError: () => void;
  // Dual-role support (Phase 3)
  isAlsoCustomer: boolean;
  customerInfo: Customer | null;
  canAccessCustomerApp: boolean;
  switchToCustomerApp: () => Promise<string | null>; // Returns customer app URL
  // Enhanced partner context
  managedCustomers: Customer[];
  assignedCustomers: Customer[]; // Customers where user is Account Manager
  hasPartnerAccess: boolean;
  isPartnerAdmin: boolean; // Whether current user is admin in PartnerUsers
  currentUserId: string | null; // Current user ID
  updateManagedCustomer: (customer: Customer) => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

function AuthProviderInner({ children }: AuthProviderProps) {
  const { instance, accounts } = useMsal();
  const signedIn = currentAccount() ?? accounts[0] ?? null;
  const account = useAccount(signedIn || {});
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [partnerAuthContext, setPartnerAuthContext] = useState<PartnerAuthContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Dual-role state (Phase 3)
  const [isAlsoCustomer, setIsAlsoCustomer] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<Customer | null>(null);
  const [canAccessCustomerApp, setCanAccessCustomerApp] = useState(false);
  const [managedCustomers, setManagedCustomers] = useState<Customer[]>([]);
  const [assignedCustomers, setAssignedCustomers] = useState<Customer[]>([]);
  const [hasPartnerAccess, setHasPartnerAccess] = useState(false);
  const [isPartnerAdmin, setIsPartnerAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const updateManagedCustomer = useCallback((updatedCustomer: Customer) => {
    setManagedCustomers((currentCustomers) =>
      currentCustomers.map((customer) =>
        customer.customerId === updatedCustomer.customerId
          ? { ...customer, ...updatedCustomer }
          : customer
      )
    );

    setAssignedCustomers((currentCustomers) =>
      currentCustomers.map((customer) =>
        customer.customerId === updatedCustomer.customerId
          ? { ...customer, ...updatedCustomer }
          : customer
      )
    );

    setPartnerAuthContext((currentContext) => {
      if (!currentContext) {
        return currentContext;
      }

      return {
        ...currentContext,
        managedCustomers: currentContext.managedCustomers.map((customer) =>
          customer.customerId === updatedCustomer.customerId
            ? { ...customer, ...updatedCustomer }
            : customer
        ),
        assignedCustomers: currentContext.assignedCustomers.map((customer) =>
          customer.customerId === updatedCustomer.customerId
            ? { ...customer, ...updatedCustomer }
            : customer
        ),
      };
    });

    setCustomerInfo((currentCustomerInfo) =>
      currentCustomerInfo?.customerId === updatedCustomer.customerId
        ? { ...currentCustomerInfo, ...updatedCustomer }
        : currentCustomerInfo
    );
  }, []);
  
  // Token coordination state to prevent multiple concurrent requests
  const [profileLoadingPromise, setProfileLoadingPromise] = useState<Promise<void> | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        // DEV MODE: Bypass authentication for local UI development
        if (config.features.devMode) {
          setIsAuthenticated(true);
          setPartner(mockPartnerProfile);
          setPartnerAuthContext(mockPartnerAuthContext);
          setHasPartnerAccess(true);
          setIsAlsoCustomer(mockPartnerAuthContext.isAlsoCustomer);
          setCustomerInfo(mockPartnerAuthContext.customerInfo || null);
          setCanAccessCustomerApp(mockPartnerAuthContext.canAccessCustomerView);
          setManagedCustomers(mockPartnerAuthContext.managedCustomers);
          setAssignedCustomers(mockPartnerAuthContext.assignedCustomers);
          setIsPartnerAdmin(mockPartnerAuthContext.isPartnerAdmin);
          setCurrentUserId(mockPartnerAuthContext.partnerUserId);
          // Set mock token for API service (though it won't be used in dev mode)
          apiService.setAuthToken(mockAccessToken);
          setIsLoading(false);
          return;
        }
        
        // PRODUCTION MODE: Normal authentication flow
        // Handle redirect promise (MSAL is already initialized in App.tsx)
        const response = await instance.handleRedirectPromise();
        if (response) {
          setIsAuthenticated(true);
          // FIXED: Don't use login token directly, get a fresh one to avoid signature validation issues
          await loadPartnerProfile(); // Let it get a fresh token automatically
        } else if (account) {
          // User is already logged in
          const token = await getAccessToken();
          if (token) {
            setIsAuthenticated(true);
            await loadPartnerProfile(token);
          }
        }
      } catch (err) {
        setError('Authentication initialization failed');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, account]);

  // Load partner profile and context - FIXED: Prevents multiple concurrent requests
  const loadPartnerProfile = useCallback(async (accessToken?: string, retryCount: number = 0) => {
    // FIXED: If there's already a loading operation in progress, return it to prevent multiple concurrent requests
    if (profileLoadingPromise && retryCount === 0) {
      return profileLoadingPromise;
    }
    
    // FIXED: Cancel any pending retry timeout to prevent stale retries
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    const maxRetries = 3;
    const baseDelay = 1000;
    
    const loadOperation = async () => {
      try {
        // FIXED: Always get fresh token instead of using potentially stale passed token
        let freshToken: string | null = accessToken || null;
        if (!freshToken) {
          freshToken = await getAccessToken();
          if (!freshToken) {
            // FALLBACK: Try to use basic scopes if available
            const accounts = instance.getAllAccounts();
            const signedIn = instance.getActiveAccount() ?? accounts[0];
            if (signedIn) {
              try {
                const fallbackRequest = {
                  scopes: ['openid', 'profile', 'email'],
                  account: signedIn,
                };
                const fallbackResponse = await instance.acquireTokenSilent(fallbackRequest);
                freshToken = fallbackResponse.accessToken;
              } catch (fallbackErr) {

              }
            }
            
            if (!freshToken) {
              throw new Error('No access token available - both primary and fallback methods failed');
            }
          }
        }
        
        apiService.setAuthToken(freshToken);
        
        // Load partner authentication context from backend
        const authContextResponse = await partnerService.getPartnerAuthContext();
        
        if (authContextResponse.success && authContextResponse.data) {
          const authCtx = authContextResponse.data;
          
          // Set partner authentication context
          setPartnerAuthContext(authCtx);
          setHasPartnerAccess(true);
          
          // Set dual-role state
          setIsAlsoCustomer(authCtx.isAlsoCustomer || false);
          setCustomerInfo(authCtx.customerInfo || null);
          setCanAccessCustomerApp(authCtx.canAccessCustomerView || false);
          setManagedCustomers(authCtx.managedCustomers || []);
          setAssignedCustomers(authCtx.assignedCustomers || []);
          setIsPartnerAdmin(authCtx.isPartnerAdmin || false);
          setCurrentUserId(authCtx.partnerUserId || null);

          // Create comprehensive partner profile from auth context
          const partnerProfile: PartnerProfile = {
            partnerId: authCtx.partnerInfo.partnerId,
            partnerName: authCtx.partnerInfo.partnerName,
            adminFirstName: authCtx.partnerInfo.adminFirstName,
            adminLastName: authCtx.partnerInfo.adminLastName,
            adminEmail: authCtx.partnerInfo.adminEmail,
            adminContactNumber: authCtx.partnerInfo.adminContactNumber || '',
            country: authCtx.partnerInfo.country || '',
            status: authCtx.partnerInfo.status,
            createdAt: authCtx.partnerInfo.createdAt,
            website: authCtx.partnerInfo.website || '',
            partnerEmail: authCtx.partnerInfo.partnerEmail || '',
            partnerContactNumber: authCtx.partnerInfo.partnerContactNumber || '',
            managedCustomerCount: authCtx.managedCustomers?.length || 0,
            isAlsoCustomer: authCtx.isAlsoCustomer || false,
            permissions: {
              canManageCustomers: true,
              canViewReports: true,
              canExportData: true,
              canAccessCustomerApp: authCtx.canAccessCustomerView || false,
            },
          };

          setPartner(partnerProfile);

          // Clear any previous errors on success
          setError(null);
          setProfileLoadingPromise(null); // Clear loading state

        } else {
          // Check if user is not a partner
          if (authContextResponse.error && authContextResponse.error.includes('Partner not found')) {
            setHasPartnerAccess(false);
            throw new Error('You are not registered as a partner. Please contact your administrator.');
          }
          throw new Error('Failed to get partner context from backend');
        }

      } catch (err) {
        // Check if this is a temporary authentication error that might succeed on retry
        const isRetryableError = (err && typeof err === 'object') && 
          (('status' in err && (err.status === 401 || err.status === 403)) ||
          ('message' in err && typeof err.message === 'string' && err.message.includes('Network error')));
        
        if (isRetryableError && retryCount < maxRetries) {
          const delay = baseDelay * Math.pow(2, retryCount);
          // FIXED: Store timeout reference to allow cancellation
          retryTimeoutRef.current = setTimeout(() => {
            retryTimeoutRef.current = null;
            // FIXED: Don't pass stale token to retry, let it get fresh one
            loadPartnerProfile(undefined, retryCount + 1);
          }, delay);
          return;
        }
        
        // All retries exhausted or non-retryable error
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to load partner profile: ${errorMsg}`);
        setHasPartnerAccess(false);
        setProfileLoadingPromise(null); // Clear loading state
      }
    };
    
    // FIXED: Store the promise to prevent multiple concurrent operations
    if (retryCount === 0) {
      const promise = loadOperation();
      setProfileLoadingPromise(promise);
      return promise;
    } else {
      return loadOperation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAccessToken is stable and defined later
  }, [profileLoadingPromise]);

  // Login function
  const login = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // DEV MODE: Instant mock login
      if (config.features.devMode) {
        setIsAuthenticated(true);
        setPartner(mockPartnerProfile);
        setPartnerAuthContext(mockPartnerAuthContext);
        setHasPartnerAccess(true);
        setIsAlsoCustomer(mockPartnerAuthContext.isAlsoCustomer);
        setCustomerInfo(mockPartnerAuthContext.customerInfo || null);
        setCanAccessCustomerApp(mockPartnerAuthContext.canAccessCustomerView);
        setManagedCustomers(mockPartnerAuthContext.managedCustomers);
        setAssignedCustomers(mockPartnerAuthContext.assignedCustomers);
        setIsPartnerAdmin(mockPartnerAuthContext.isPartnerAdmin);
        setCurrentUserId(mockPartnerAuthContext.partnerUserId);
        apiService.setAuthToken(mockAccessToken);
        setIsLoading(false);
        return;
      }
      
      // PRODUCTION MODE: Normal Azure AD login. The page navigates to Microsoft;
      // initialiseMsal and initializeAuth pick up the result when it returns.
      await instance.loginRedirect(loginRequest);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithAnotherAccount = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      if (config.features.devMode) {
        setIsLoading(false);
        return;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      setIsAuthenticated(false);
      setPartner(null);
      setPartnerAuthContext(null);
      setHasPartnerAccess(false);
      setProfileLoadingPromise(null);
      apiService.clearAuthToken();
      clearAitryxCoreTokenCache();
      partnerService.clearAuthContextCache();
      instance.setActiveAccount(null);
      await instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
    } catch (err: any) {
      setError(err.message || 'Could not switch account');
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Cancel any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      
      // Clear all state
      setIsAuthenticated(false);
      setPartner(null);
      setPartnerAuthContext(null);
      setIsAlsoCustomer(false);
      setCustomerInfo(null);
      setCanAccessCustomerApp(false);
      setManagedCustomers([]);
      setAssignedCustomers([]);
      setHasPartnerAccess(false);
      setIsPartnerAdmin(false);
      setCurrentUserId(null);
      setProfileLoadingPromise(null);
      setError(null);
      
      // Clear API token and cached AitryxCore JWT
      apiService.clearAuthToken();
      clearAitryxCoreTokenCache();
      
      // Clear partner service auth context cache
      partnerService.clearAuthContextCache();
      
      // MSAL logout
      await instance.logoutRedirect();
    } catch (err: any) {
      setError(err.message || 'Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Get access token - FIXED: Use MSAL instance directly to avoid account hook race condition
  const getAccessToken = async (): Promise<string | null> => {
    // DEV MODE: Return mock token
    if (config.features.devMode) {
      return mockAccessToken;
    }
    
    try {
      // FIXED: Get account directly from MSAL instance instead of relying on hook
      const msalAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!msalAccount) {
        return null;
      }
      const silentRequest = {
        ...apiScopes,
        account: msalAccount,
      };
      
      const response = await instance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        try {
          // Navigates away; the token is picked up after Microsoft redirects back.
          await instance.acquireTokenRedirect(apiScopes);
        } catch (redirectErr) {
          return null;
        }
      }
      
      return null;
    }
  };

  // Switch to customer app (Phase 3 dual-role support)
  const switchToCustomerApp = async (): Promise<string | null> => {
    try {
      if (!isAlsoCustomer || !customerInfo) {
        throw new Error('User is not authorized to access customer app');
      }

      // Get customer access information
      const response = await partnerService.validatePartnerCustomerAccess();
      if (response.success && response.data?.canAccessCustomerApp) {
        // Construct customer app URL
        const customerAppUrl = `${window.location.origin.replace('partner', 'customer')}/?customer=${customerInfo.customerId}`;
        return customerAppUrl;
      }
      
      throw new Error('Cannot access customer app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch to customer app');
      return null;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    partner,
    partnerAuthContext,
    account: config.features.devMode ? mockAccount : account, // Use mock account in dev mode
    error,
    login,
    logout,
    signInWithAnotherAccount,
    getAccessToken,
    clearError,
    // Dual-role support (Phase 3)
    isAlsoCustomer,
    customerInfo,
    canAccessCustomerApp,
    switchToCustomerApp,
    // Enhanced partner context
    managedCustomers,
    assignedCustomers,
    hasPartnerAccess,
    isPartnerAdmin,
    currentUserId,
    updateManagedCustomer,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Main Auth Provider with MSAL wrapper
export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>
        {children}
      </AuthProviderInner>
    </MsalProvider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthProvider; 